import type { ReactNode } from "react"
import {
  CheckCircle2Icon,
  CircleAlertIcon,
  InfoIcon,
  LoaderCircleIcon,
} from "lucide-react"

import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@f2e/ui/components/base"

export type AuthLinkResultVariant = "loading" | "success" | "error" | "info"

export interface AuthLinkResultAction {
  label: string
  onClick: () => void
  variant?: "default" | "outline" | "destructive"
  disabled?: boolean
}

export interface AuthLinkResultCardProps {
  title: string
  description: ReactNode
  variant?: AuthLinkResultVariant
  primaryAction?: AuthLinkResultAction
  secondaryAction?: AuthLinkResultAction
  maxWidth?: "sm" | "md"
}

const variantConfig = {
  loading: {
    icon: LoaderCircleIcon,
    iconClassName: "text-slate-500 animate-spin",
  },
  success: {
    icon: CheckCircle2Icon,
    iconClassName: "text-emerald-600",
  },
  error: {
    icon: CircleAlertIcon,
    iconClassName: "text-destructive",
  },
  info: {
    icon: InfoIcon,
    iconClassName: "text-slate-500",
  },
}

const maxWidthClassName = {
  sm: "max-w-sm",
  md: "max-w-md",
}

const AuthLinkResultCard = ({
  title,
  description,
  variant = "info",
  primaryAction,
  secondaryAction,
  maxWidth = "sm",
}: AuthLinkResultCardProps) => {
  const { icon: Icon, iconClassName } = variantConfig[variant]

  return (
    <Card
      className={`w-full ${maxWidthClassName[maxWidth]}`}
      role={variant === "error" ? "alert" : "status"}
      aria-live="polite"
    >
      <CardHeader className="items-center text-center">
        <Icon className={`mb-1 h-8 w-8 ${iconClassName}`} aria-hidden />
        <CardTitle className="text-2xl">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      {(primaryAction || secondaryAction) && (
        <CardContent className="grid gap-3">
          {primaryAction && (
            <Button
              className="w-full"
              variant={primaryAction.variant}
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
            >
              {primaryAction.label}
            </Button>
          )}
          {secondaryAction && (
            <Button
              className="w-full"
              variant={secondaryAction.variant ?? "outline"}
              onClick={secondaryAction.onClick}
              disabled={secondaryAction.disabled}
            >
              {secondaryAction.label}
            </Button>
          )}
        </CardContent>
      )}
    </Card>
  )
}

export default AuthLinkResultCard
